import React, { createContext, useState, useContext, useEffect } from 'react';
import { ProfileService } from '../services/ProfileService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => 
    localStorage.getItem('llap_access_token') || localStorage.getItem('llap_auth_token') || null
  );
  const [refreshToken, setRefreshToken] = useState(() => 
    localStorage.getItem('llap_refresh_token') || null
  );

  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return null;
    try {
      const parsed = JSON.parse(savedUser);
      return {
        ...parsed,
        role: parsed.role || parsed.userRole || 'student',
        studentClassification: parsed.studentClassification || parsed.studentType || 'PUBLIC'
      };
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hydrateUserProfile = async () => {
      const tokenToUse = accessToken || localStorage.getItem('llap_access_token') || localStorage.getItem('llap_auth_token');
      if (tokenToUse) {
        try {
          const profileData = await ProfileService.getProfile(tokenToUse);
          if (profileData && profileData.success && profileData.profile) {
            const rawProfile = profileData.profile;
            const normalizedProfile = {
              ...rawProfile,
              role: rawProfile.role || rawProfile.userRole || (rawProfile.isInstructor ? 'teacher' : 'student'),
              studentClassification: rawProfile.studentClassification || rawProfile.studentType || (rawProfile.isInstructor ? 'INSTRUCTOR' : 'PUBLIC')
            };

            setUser(normalizedProfile);
            localStorage.setItem('user', JSON.stringify(normalizedProfile));
          } else if (profileData && profileData.status === 401) {
            console.warn('Session hydration notice: 401 Unauthorized received from server. Clearing session.');
            logout();
          } else {
            console.warn('Session hydration notice: Profile fetch returned non-standard payload. Retaining local session fallback.');
          }
        } catch (err) {
          console.warn('Session hydration network/parsing notice:', err.message);
          const isExplicitUnauthorized = err.status === 401 || (err.message && err.message.includes('401'));
          const isNetworkRefusal = err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.message.includes('ERR_CONNECTION_REFUSED'));

          if (isExplicitUnauthorized && !isNetworkRefusal) {
            console.warn('Confirmed 401 Unauthorized. Clearing session.');
            logout();
          } else {
            console.warn('Network refusal or temporary backend error detected. Retaining local session to prevent forced logout.');
          }
        }
      }
      setLoading(false);
    };

    hydrateUserProfile();
  }, [accessToken]);

  const login = (userData, newAccessToken, newRefreshToken) => {
    const normalizedUserData = userData ? {
      ...userData,
      role: userData.role || userData.userRole || 'student',
      studentClassification: userData.studentClassification || userData.studentType || 'PUBLIC'
    } : null;

    setUser(normalizedUserData);
    if (normalizedUserData) {
      localStorage.setItem('user', JSON.stringify(normalizedUserData));
    } else {
      localStorage.removeItem('user');
    }

    if (newAccessToken) {
      setAccessToken(newAccessToken);
      localStorage.setItem('llap_access_token', newAccessToken);
      localStorage.setItem('llap_auth_token', newAccessToken);
    }
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
      localStorage.setItem('llap_refresh_token', newRefreshToken);
    }
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('llap_access_token');
    localStorage.removeItem('llap_refresh_token');
    localStorage.removeItem('llap_auth_token');
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, refreshToken, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;